// components/layout/NotificationBell.tsx
// In-app notification center (bell + dropdown). Reads the framework's
// notification API via jasit-api-kit. Styling is driven by a machine-readable
// `kind` (content.icon) — not by parsing Swedish title text — with a legacy
// title/deep-link fallback for older notifications. Renders labeled actions
// (Proposal 27) from content.actions. Two-tier state: the badge counts UNSEEN
// (clears when the panel opens); rows stay highlighted until actually read.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInvitationsLauncher } from '@/contexts/InvitationsLauncherContext';
import {
    Bell, CheckCheck, FileText, Mail, GraduationCap, BookOpen, Briefcase, Users,
    Building2, PartyPopper, Trash2, Loader2, Clock, ChevronRight,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { UserNotificationWithDetails, NotificationAction } from 'jasit-api-kit';
import { getNotificationActions } from 'jasit-api-kit';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getNotificationApi, getNotificationsRealtime } from '@/lib/api/client';
import { playNotificationChime } from '@/lib/notificationSound';

const POLL_MS = 60_000;
const PAGE_SIZE = 20;

// Short relative time, Swedish: "nyss", "5 min", "2 tim", "3 dgr", or a date.
function relativeTime(iso?: string): string {
    if (!iso) return '';
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '';
    const diffMs = Date.now() - then;
    const min = Math.floor(diffMs / 60_000);
    if (min < 1) return 'nyss';
    if (min < 60) return `${min} min`;
    const hrs = Math.floor(min / 60);
    if (hrs < 24) return `${hrs} tim`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days} dgr`;
    return new Date(iso).toLocaleDateString('sv-SE');
}

// Coarse time bucket for the section headers in the list.
function groupLabel(iso?: string): string {
    if (!iso) return 'Tidigare';
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return 'Tidigare';
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    if (then >= startOfToday) return 'Idag';
    if (then >= startOfToday - 86_400_000) return 'Igår';
    if (then >= startOfToday - 6 * 86_400_000) return 'Denna vecka';
    return 'Tidigare';
}

const contentOf = (n: UserNotificationWithDetails): Record<string, unknown> | undefined =>
    (n.content as Record<string, unknown> | undefined) ?? undefined;

const actionUrlOf = (n: UserNotificationWithDetails): string | undefined => {
    const v = contentOf(n)?.action_url;
    return typeof v === 'string' ? v : undefined;
};

// ── Styling by KIND ──────────────────────────────────────────────────────────
// The backend stamps a stable `kind` into content.icon (see notification_emit.go
// Kind* constants). We map kind → domain ICON + nature COLOUR here, in ONE place,
// so a copy tweak or localization can never break styling. Notifications without
// a known kind fall back to deriving both from the (Swedish) title + deep-link.
type Nature = 'created' | 'positive' | 'destructive' | 'warning' | 'info';

const NATURE_COLOR: Record<Nature, string> = {
    created: 'text-[#1A4D4F] bg-[#A4C3B2]/40', // brand teal — something new
    positive: 'text-green-600 bg-green-50',     // accepted / completed / welcome
    destructive: 'text-red-600 bg-red-50',      // cancelled / withdrawn / rejected
    warning: 'text-amber-600 bg-amber-50',      // expiring / reminder / filled-closed
    info: 'text-blue-600 bg-blue-50',           // in progress (under review)
};

// Icons match the app-wide canonical set (Sidebar).
const KIND_STYLE: Record<string, { icon: LucideIcon; nature: Nature }> = {
    application_new: { icon: FileText, nature: 'created' },
    application_reviewing: { icon: FileText, nature: 'info' },
    application_accepted: { icon: FileText, nature: 'positive' },
    application_rejected: { icon: FileText, nature: 'destructive' },
    application_withdrawn: { icon: FileText, nature: 'destructive' },
    need_filled: { icon: Briefcase, nature: 'warning' },
    need_expired: { icon: Clock, nature: 'warning' },
    invitation_received: { icon: Mail, nature: 'created' },
    invitation_accepted: { icon: Mail, nature: 'positive' },
    invitation_cancelled: { icon: Mail, nature: 'destructive' },
    invitation_expiring: { icon: Clock, nature: 'warning' },
    invitation_expired: { icon: Mail, nature: 'destructive' },
    employed: { icon: Building2, nature: 'positive' },
    colleague_joined: { icon: Users, nature: 'created' },
    onboarding_assigned: { icon: GraduationCap, nature: 'created' },
    onboarding_not_started: { icon: GraduationCap, nature: 'warning' },
    course_completed: { icon: BookOpen, nature: 'positive' },
    posting_match: { icon: Briefcase, nature: 'created' },
    welcome: { icon: PartyPopper, nature: 'positive' },
    admin_contact: { icon: Building2, nature: 'created' },
    admin_org: { icon: Building2, nature: 'created' },
    admin_unit: { icon: Building2, nature: 'created' },
    admin_employer: { icon: Users, nature: 'created' },
};

// ── Legacy fallback (pre-kind notifications) ─────────────────────────────────
type Domain = 'applications' | 'invitations' | 'onboarding' | 'course' | 'postings' | 'workplace' | 'admin' | 'welcome' | 'other';
const DOMAIN_ICON: Record<Domain, LucideIcon> = {
    applications: FileText, invitations: Mail, onboarding: GraduationCap, course: BookOpen,
    postings: Briefcase, workplace: Users, admin: Building2, welcome: PartyPopper, other: Bell,
};
function natureOf(t: string): Nature {
    if (/godkänd|accepterad|slutförd|välkommen/.test(t)) return 'positive';
    if (/återkallad|avslut|gått ut|tillsatt|besked|avslag/.test(t)) return 'destructive';
    if (/snart ut|påminnelse|väntar|expir/.test(t)) return 'warning';
    if (/granskas/.test(t)) return 'info';
    return 'created';
}
function domainOf(t: string, url: string): Domain {
    if (/välkommen/.test(t)) return 'welcome';
    if (/bevakning/.test(t) || url.includes('/dashboard/jobs/')) return 'postings';
    if (/inbjudan/.test(t) || url.includes('invitation')) return 'invitations';
    if (/modul/.test(t)) return 'course';
    if (/onboarding/.test(t) || url.includes('/onboarding')) return 'onboarding';
    if (/ansökan|tjänst/.test(t) || url.includes('/applications') || url.includes('/my-applications')) return 'applications';
    if (/kollega|anställning/.test(t) || url.includes('/my-workplace')) return 'workplace';
    if (/kontaktförfrågan|organisation|enhet|arbetsgivare/.test(t) || url.includes('/contacts') || url.includes('/organisations')) return 'admin';
    return 'other';
}

function iconFor(n: UserNotificationWithDetails): { Icon: LucideIcon; className: string } {
    // Kind first (content.icon carries the machine-readable kind).
    const kind = contentOf(n)?.icon;
    if (typeof kind === 'string' && KIND_STYLE[kind]) {
        const k = KIND_STYLE[kind];
        return { Icon: k.icon, className: NATURE_COLOR[k.nature] };
    }
    // Legacy fallback: derive from title + deep-link.
    const t = (n.title || '').toLowerCase();
    const url = actionUrlOf(n) || '';
    const domain = domainOf(t, url);
    const nature: Nature = domain === 'welcome' ? 'positive' : natureOf(t);
    return { Icon: DOMAIN_ICON[domain], className: NATURE_COLOR[nature] };
}

const isUnread = (n: UserNotificationWithDetails) => n.status !== 'read';

const NotificationBell: React.FC = () => {
    const navigate = useNavigate();
    const { openInvitations } = useInvitationsLauncher();
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState<UserNotificationWithDetails[]>([]);
    const [unseen, setUnseen] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    // Arrival cue: swing the bell + pop the badge when the UNSEEN count rises.
    const [arrived, setArrived] = useState(false);
    const prevUnseenRef = useRef<number | null>(null);
    const cueTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const pulseCue = useCallback(() => {
        if (cueTimerRef.current) clearTimeout(cueTimerRef.current);
        setArrived(true);
        playNotificationChime();
        cueTimerRef.current = setTimeout(() => setArrived(false), 900);
    }, []);

    // Badge = UNSEEN count (two-tier: opening the panel marks all seen → badge
    // clears; rows stay highlighted until actually read). refreshCount also fires
    // the arrival cue when the count rises.
    const refreshCount = useCallback(async () => {
        try {
            const res = await getNotificationApi().getUnseenCount();
            const count = res?.count ?? 0;
            const prev = prevUnseenRef.current;
            prevUnseenRef.current = count;
            setUnseen(count);
            if (prev !== null && count > prev) pulseCue();
        } catch {
            /* silent — best-effort UX */
        }
    }, [pulseCue]);

    const loadList = useCallback(async () => {
        setLoading(true);
        try {
            const list = await getNotificationApi().getMyNotifications({ page: 1, limit: PAGE_SIZE });
            setItems(list ?? []);
            setPage(1);
            setHasMore((list?.length ?? 0) >= PAGE_SIZE);
            return list ?? [];
        } catch {
            setItems([]);
            setHasMore(false);
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    const loadMore = useCallback(async () => {
        setLoadingMore(true);
        try {
            const next = page + 1;
            const list = await getNotificationApi().getMyNotifications({ page: next, limit: PAGE_SIZE });
            setItems((prev) => [...prev, ...(list ?? [])]);
            setPage(next);
            setHasMore((list?.length ?? 0) >= PAGE_SIZE);
        } catch {
            /* keep what we have */
        } finally {
            setLoadingMore(false);
        }
    }, [page]);

    const handleDelete = async (e: React.MouseEvent, n: UserNotificationWithDetails) => {
        e.stopPropagation();
        try {
            await getNotificationApi().delete(n.id);
            setItems((prev) => prev.filter((x) => x.id !== n.id));
        } catch { /* ignore */ }
    };

    const handleClearAll = async () => {
        try {
            await getNotificationApi().deleteAll();
            setItems([]);
            setUnseen(0);
            prevUnseenRef.current = 0;
            setHasMore(false);
        } catch { /* ignore */ }
    };

    // Poll unseen on mount + interval (backstop for the realtime channel).
    useEffect(() => {
        refreshCount();
        const t = setInterval(refreshCount, POLL_MS);
        return () => {
            clearInterval(t);
            if (cueTimerRef.current) clearTimeout(cueTimerRef.current);
        };
    }, [refreshCount]);

    // Real-time delivery: a pushed notification re-reads the authoritative unseen
    // count (fires the cue on a rise), and prepends the item so it appears live.
    useEffect(() => {
        const rt = getNotificationsRealtime();
        rt.onNotification = (payload) => {
            refreshCount();
            const n = payload.notification;
            setItems((prev) => {
                if (prev.some((x) => x.id === payload.user_notification_id)) return prev;
                const item: UserNotificationWithDetails = {
                    id: payload.user_notification_id,
                    notification_id: n.id,
                    user_id: '',
                    status: 'sent',
                    created_at: n.created_at ?? new Date().toISOString(),
                    type: n.type,
                    title: n.title,
                    message: n.message,
                    content: n.content,
                    priority: n.priority,
                };
                return [item, ...prev];
            });
        };
        rt.onStateChange = (state) => {
            if (state.status === 'connected') refreshCount();
        };
        rt.connect();
        return () => {
            rt.onNotification = undefined;
            rt.onStateChange = undefined;
            rt.disconnect();
        };
    }, [refreshCount]);

    // #296 — which notifications belong in the OLÄSTA section, snapshotted when the
    // panel opens.
    //
    // The section a row sits in must not change while you are looking at it.
    // Reading a notification moves it to "Lästa", and if that happened live the row
    // would jump out from under the cursor the instant it was clicked — the same
    // mistake as collapsing a card to a different place (#290). So a row that was
    // unread when the panel opened STAYS in the unread section for this viewing,
    // and the list re-sorts next time the bell is opened.
    //
    // A notification arriving while the panel is open is genuinely new and unread,
    // so it joins the unread section immediately — hence the `|| isUnread(n)`.
    const unreadAtOpenRef = useRef<Set<string>>(new Set());

    // On open: load the list AND mark everything seen (clears the badge; rows stay
    // highlighted until read).
    useEffect(() => {
        if (!open) return;
        // Snapshot from the FRESHLY loaded list, not from whatever `items` happens to
        // hold — on the first open it holds nothing. Until it resolves the render
        // falls back to the live `isUnread`, so nothing flashes into the wrong
        // section in the meantime.
        void loadList().then((fresh) => {
            unreadAtOpenRef.current = new Set(fresh.filter(isUnread).map((n) => n.id));
        });
        if (unseen > 0) {
            getNotificationApi().markAllAsSeen().catch(() => { /* ignore */ });
            setUnseen(0);
            prevUnseenRef.current = 0;
        }
    }, [open, loadList]); // eslint-disable-line react-hooks/exhaustive-deps

    // Navigate to a deep-link (invitations open the slide-over instead of a page).
    const navigateTo = useCallback((url?: string) => {
        if (!url) return;
        setOpen(false);
        if (url === '/dashboard/my-invitations') { openInvitations(); return; }
        navigate(url);
    }, [navigate, openInvitations]);

    const markRead = useCallback(async (n: UserNotificationWithDetails) => {
        if (!isUnread(n)) return;
        try {
            await getNotificationApi().markAsRead(n.id);
            setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, status: 'read' as const } : x)));
        } catch { /* ignore */ }
    }, []);

    // Whole-row click → primary action (first action's url, else legacy action_url).
    const handleOpenItem = async (n: UserNotificationWithDetails) => {
        await markRead(n);
        const primary = getNotificationActions(contentOf(n))[0];
        navigateTo(primary?.url ?? actionUrlOf(n));
    };

    // A labeled action button. Link actions navigate; imperative actions call the
    // API in place (Proposal 27 phase 2), then refresh.
    const runAction = async (e: React.MouseEvent, n: UserNotificationWithDetails, action: NotificationAction) => {
        e.stopPropagation();
        void markRead(n);
        if (action.url) { navigateTo(action.url); return; }
        if (action.method && action.endpoint) {
            if (action.confirm && !window.confirm(action.confirm)) return;
            try {
                await getNotificationApi().executeAction(action);
                await loadList();
                refreshCount();
            } catch { /* ignore — best-effort */ }
        }
    };

    const handleMarkAll = async () => {
        try {
            await getNotificationApi().markAllAsRead();
            setItems((prev) => prev.map((n) => ({ ...n, status: 'read' as const })));
            setUnseen(0);
            prevUnseenRef.current = 0;
        } catch { /* ignore */ }
    };

    const badge = unseen > 9 ? '9+' : String(unseen);

    // #296 — unread first, then a "Lästa" section. Reading a notification moves it
    // down; it does not vanish, so nothing is lost and the list still empties as
    // you work through it.
    //
    // A row that was unread when the panel opened counts as unread for this whole
    // viewing (unreadAtOpenRef), so clicking one does not yank it out from under
    // the cursor into another section mid-list. `|| isUnread` keeps genuinely new
    // arrivals — pushed over the websocket while the panel is open — at the top
    // where they belong.
    const inUnreadSection = (n: UserNotificationWithDetails) =>
        unreadAtOpenRef.current.has(n.id) || isUnread(n);
    const unreadList = items.filter(inUnreadSection);
    const readList = items.filter((n) => !inUnreadSection(n));
    const ordered = [...unreadList, ...readList];
    const firstReadId = readList[0]?.id;
    // Date headers stay in the unread section only: under "Lästa" they are history
    // rather than "what is new", and each row already carries its own timestamp.
    let lastGroup = '';

    return (
        <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
            <DropdownMenuTrigger asChild>
                <button
                    className="relative h-full px-3.5 flex items-center justify-center hover:bg-gray-100 transition-colors outline-none"
                    aria-label={unseen > 0 ? `Notiser (${unseen} nya)` : 'Notiser'}
                >
                    <Bell className={`w-5 h-5 text-gray-600 origin-top ${arrived ? 'animate-bell-swing' : ''}`} />
                    {unseen > 0 && (
                        <span className="absolute top-2.5 right-2 min-w-[18px] h-[18px] flex items-center justify-center">
                            {arrived && (
                                <span className="absolute inset-0 rounded-full bg-red-400 animate-ping" aria-hidden="true" />
                            )}
                            <span className={`relative min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center ${arrived ? 'animate-badge-pop' : ''}`}>
                                {badge}
                            </span>
                        </span>
                    )}
                    <span className="sr-only" role="status" aria-live="polite">
                        {arrived ? `Du har ${unseen} nya notiser` : ''}
                    </span>
                </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent className="w-[22rem] sm:w-[30rem] rounded-2xl shadow-2xl p-0" align="start" sideOffset={12} collisionPadding={16}>
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <p className="font-semibold text-gray-900">Notiser</p>
                    <div className="flex items-center gap-3">
                        {items.some(isUnread) && (
                            <button
                                onClick={handleMarkAll}
                                className="flex items-center gap-1 text-xs font-medium text-[#1A4D4F] hover:underline"
                            >
                                <CheckCheck className="w-3.5 h-3.5" /> Markera alla lästa
                            </button>
                        )}
                        {items.length > 0 && (
                            <button
                                onClick={handleClearAll}
                                className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-red-600 hover:underline"
                            >
                                <Trash2 className="w-3.5 h-3.5" /> Rensa
                            </button>
                        )}
                    </div>
                </div>

                {/* List */}
                <div className="max-h-[60vh] overflow-y-auto">
                    {loading && items.length === 0 ? (
                        <p className="px-4 py-6 text-sm text-gray-400 text-center">Laddar…</p>
                    ) : items.length === 0 ? (
                        <div className="px-4 py-8 text-center">
                            <Bell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                            <p className="text-sm text-gray-500">Inga notiser ännu</p>
                        </div>
                    ) : (
                        ordered.map((n, idx) => {
                            const { Icon, className } = iconFor(n);
                            const actions = getNotificationActions(contentOf(n));
                            const unread = isUnread(n);
                            const inRead = idx >= unreadList.length;
                            const grp = groupLabel(n.created_at);
                            const showHeader = !inRead && grp !== lastGroup;
                            if (!inRead) lastGroup = grp;
                            return (
                            <React.Fragment key={n.id}>
                                {n.id === firstReadId && (
                                    <div className="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wide text-gray-400 bg-gray-50/60">
                                        Lästa
                                    </div>
                                )}
                                {showHeader && (
                                    <div className="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wide text-gray-400 bg-gray-50/60">{grp}</div>
                                )}
                                <div
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => handleOpenItem(n)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleOpenItem(n); }}
                                    className={`group relative w-full text-left px-4 py-3 flex gap-3 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0 cursor-pointer ${
                                        unread ? 'bg-[#A4C3B2]/10' : ''
                                    }`}
                                >
                                    <span className={`mt-0.5 w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${className}`}>
                                        <Icon className="w-[18px] h-[18px]" />
                                    </span>
                                    <span className="min-w-0 flex-1">
                                        <span className="flex items-center justify-between gap-2">
                                            <span className="text-sm font-semibold text-gray-900 truncate">{n.title}</span>
                                            <span className="flex items-center gap-1.5 flex-shrink-0">
                                                <span className="text-[11px] text-gray-400">{relativeTime(n.created_at)}</span>
                                                {unread && <span className="w-2 h-2 rounded-full bg-[#1A4D4F]" />}
                                            </span>
                                        </span>
                                        <span className="block text-sm text-gray-600 line-clamp-2">{n.message}</span>
                                        {actions.length > 0 && (
                                            <span className="mt-2 flex items-center gap-1.5 flex-wrap">
                                                {actions.map((a, i) => (
                                                    <button
                                                        key={i}
                                                        onClick={(e) => runAction(e, n, a)}
                                                        className={
                                                            (i === 0 && a.style !== 'secondary') || a.style === 'primary'
                                                                ? 'inline-flex items-center gap-1 text-xs font-semibold text-[#1A4D4F] bg-[#A4C3B2]/40 rounded-lg px-2.5 py-1.5 hover:bg-[#A4C3B2]/60 transition-colors'
                                                                : `inline-flex items-center gap-1 text-xs font-semibold rounded-lg px-2 py-1.5 transition-colors ${a.style === 'destructive' ? 'text-red-600 hover:bg-red-50' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'}`
                                                        }
                                                    >
                                                        {a.label}
                                                        {((i === 0 && a.style !== 'secondary') || a.style === 'primary') && <ChevronRight className="w-3.5 h-3.5" />}
                                                    </button>
                                                ))}
                                            </span>
                                        )}
                                    </span>
                                    <button
                                        onClick={(e) => handleDelete(e, n)}
                                        title="Ta bort"
                                        aria-label="Ta bort notis"
                                        className="self-start flex-shrink-0 p-1.5 rounded-lg text-gray-400 opacity-0 group-hover:opacity-100 hover:bg-red-50 hover:text-red-600 transition-all"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </React.Fragment>
                            );
                        })
                    )}

                    {hasMore && items.length > 0 && (
                        <button
                            onClick={loadMore}
                            disabled={loadingMore}
                            className="w-full py-2.5 text-sm font-medium text-[#1A4D4F] hover:bg-gray-50 flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            {loadingMore ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                            Visa fler
                        </button>
                    )}
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default NotificationBell;
